import { useState, useEffect, useCallback } from "react";
import { Users, Activity, CheckSquare, RefreshCw } from "lucide-react";
import Wordmark from "../components/Wordmark";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

const QUIZ_LABELS = {
  q1: { A: "15–30 min", B: "30–60 min", C: "1–2 hrs", D: "Avoids it" },
  q2: { A: "A few", B: "10–20", C: "20–50", D: "Too many" },
  q3: { A: "Nothing", B: "£100–500", C: "£500–2k", D: "£2k+" },
  q4: { A: "Daily", B: "Few/week", C: "Once/week", D: "Rarely" },
};

export default function AdminDashboard() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem("ys_admin_token"));
  const [authError, setAuthError] = useState("");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = sessionStorage.getItem("ys_admin_token");

  const fetchData = useCallback(async (t) => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        fetch(`${API_BASE}/admin/stats`, { headers: { "X-Admin-Password": t } }),
        fetch(`${API_BASE}/admin/users`, { headers: { "X-Admin-Password": t } }),
      ]);
      if (statsRes.status === 403) {
        sessionStorage.removeItem("ys_admin_token");
        setAuthed(false);
        return;
      }
      setStats(await statsRes.json());
      setUsers(await usersRes.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed && token) fetchData(token);
  }, [authed, token, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const res = await fetch(`${API_BASE}/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (res.ok) {
      sessionStorage.setItem("ys_admin_token", password);
      setAuthed(true);
      setAuthError("");
    } else {
      setAuthError("Incorrect password.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <Wordmark size="sm" darkBg={true} />
            <p className="text-gray-500 text-sm mt-2">Admin Dashboard</p>
          </div>
          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-[#FF0000] transition-all"
            />
            {authError && <p className="text-red-400 text-sm">{authError}</p>}
            <button
              type="submit"
              className="w-full py-3 bg-[#FF0000] hover:bg-[#CC0000] text-white font-black rounded-xl transition-all"
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white">
      {/* Header */}
      <div className="border-b border-[#1A1A1A] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Wordmark size="sm" darkBg={true} />
          <span className="text-gray-600 text-sm">Admin</span>
        </div>
        <button
          onClick={() => fetchData(token)}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <StatCard icon={<Users size={18} />} label="Total Users" value={stats.total_users} />
            <StatCard icon={<Activity size={18} />} label="Total Sifts" value={stats.total_sifts} />
            <StatCard icon={<CheckSquare size={18} />} label="Quiz Completions" value={stats.quiz_completions} />
          </div>
        )}

        {/* Users table */}
        <div className="bg-[#111] border border-[#1A1A1A] rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#1A1A1A]">
            <h2 className="font-bold text-sm text-gray-300 uppercase tracking-widest">Users</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1A1A1A] text-gray-500 text-xs uppercase tracking-widest">
                  <th className="text-left px-5 py-3">User ID</th>
                  <th className="text-left px-5 py-3">First Seen</th>
                  <th className="text-left px-5 py-3">Last Active</th>
                  <th className="text-left px-5 py-3">Sifts</th>
                  <th className="text-left px-5 py-3">Q1 Time</th>
                  <th className="text-left px-5 py-3">Q2 Videos</th>
                  <th className="text-left px-5 py-3">Q3 Spend</th>
                  <th className="text-left px-5 py-3">Q4 Frequency</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-gray-600">
                      No users yet.
                    </td>
                  </tr>
                )}
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A] transition-colors">
                    <td className="px-5 py-3 font-mono text-gray-400 text-xs">{u.user_id.slice(0, 12)}…</td>
                    <td className="px-5 py-3 text-gray-400">{formatDate(u.first_seen)}</td>
                    <td className="px-5 py-3 text-gray-400">{formatDate(u.last_seen)}</td>
                    <td className="px-5 py-3">
                      <span className={`font-bold ${u.sift_count >= 3 ? "text-[#FF0000]" : "text-white"}`}>
                        {u.sift_count}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-gray-400">{quizLabel("q1", u.quiz_answers?.q1)}</td>
                    <td className="px-5 py-3 text-gray-400">{quizLabel("q2", u.quiz_answers?.q2)}</td>
                    <td className="px-5 py-3 text-gray-400">{quizLabel("q3", u.quiz_answers?.q3)}</td>
                    <td className="px-5 py-3 text-gray-400">{quizLabel("q4", u.quiz_answers?.q4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="bg-[#111] border border-[#1A1A1A] rounded-xl px-5 py-4 flex items-center gap-4">
      <div className="text-[#FF0000]">{icon}</div>
      <div>
        <p className="text-gray-500 text-xs uppercase tracking-widest">{label}</p>
        <p className="text-2xl font-black text-white">{value ?? "—"}</p>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function quizLabel(q, answer) {
  if (!answer) return "—";
  return QUIZ_LABELS[q]?.[answer] ?? answer;
}
